/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║                          API KEY FLOW                                     ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 *  Three nested concepts, loaded in order, with localStorage caching at each
 *  level so reload doesn't re-fetch unnecessarily:
 *
 *  ┌──────────────────────────────────────────────────────────────────────┐
 *  │  1. ACCOUNT  (Vietmap user account; ≥1 per Keycloak user)             │
 *  │     GET /accounts                                                     │
 *  │     - state: accounts[], activeAccountId                              │
 *  │     - persist: localStorage["vmp:active_account_id"]                  │
 *  └──────────────────────────────────────────────────────────────────────┘
 *                                ↓
 *  ┌──────────────────────────────────────────────────────────────────────┐
 *  │  2. CONSUMER (workspace/tenant; ≥1 per account)                       │
 *  │     GET /accounts/{accountId}/consumers                               │
 *  │     - state: consumers[], activeConsumerId                            │
 *  │     - persist: localStorage["vmp:active_consumer_id"]                 │
 *  │     - per consumer: GET /consumers/{id}/limits (apiId=7 ⇒ map access) │
 *  └──────────────────────────────────────────────────────────────────────┘
 *                                ↓
 *  ┌──────────────────────────────────────────────────────────────────────┐
 *  │  3. API KEY  (tile credential; ≥1 per consumer)                       │
 *  │     GET /consumers/{id}/apikeys  ← LAZY: only when user picks consumer│
 *  │     - state: consumer.apiKeys                                         │
 *  │     - cache: localStorage["vmp:apikeys:{id}"] (24h TTL)               │
 *  │     - persist picked key: localStorage["vmp:active_apikey"]           │
 *  └──────────────────────────────────────────────────────────────────────┘
 *
 *  ─── Usage by route ─────────────────────────────────────────────────────
 *
 *  /create (editor):
 *    - Needs CONSUMER ID (for save/load/X-Consumer-ID header).
 *    - Needs API KEY (for map tile loading via mapStyle.metadata).
 *    - Bootstrap: loadAccounts → loadConsumers → preselect first.
 *    - App.componentDidMount reads getStoredApiKey() → applies to metadata.
 *    - 🔑 picker: account ▸ consumer ▸ apikey (3 levels).
 *
 *  /manage (style management):
 *    - Only needs CONSUMER ID (for X-Consumer-ID on list/delete/get).
 *    - Map tile rendering is N/A here.
 *    - 🔑 picker: account ▸ consumer ONLY (no apikey drill; saves /apikeys).
 *    - Picking consumer ⇒ refetch /custom-styles list.
 *
 *  ─── Pub/sub ────────────────────────────────────────────────────────────
 *
 *  Subscribers (AppToolbar, ManagePage, etc.) register via addAuthListener.
 *  emitChange() fires on:  loadAccounts complete, loadConsumers each step,
 *  loadConsumerApiKeys state changes, setActiveConsumerId/AccountId, login.
 *  Subscribers should de-dupe their own work (inflight flag) since emit
 *  fires multiple times during the cascade.
 */

import Keycloak from "keycloak-js";
import { toast } from "./toast";
import i18n from "../i18n";

// ─── Config (from Vite env vars) ──────────────────────────────────────────────
const KEYCLOAK_URL = import.meta.env.VITE_KEYCLOAK_URL as string | undefined;
const KEYCLOAK_REALM = import.meta.env.VITE_KEYCLOAK_REALM as string | undefined;
const KEYCLOAK_CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID as string | undefined;

/** Vietmap Console sign-up (new users get an account + API keys there). */
export const REGISTER_URL = (import.meta.env.VITE_REGISTER_URL as string | undefined) || "https://maps.vietmap.vn/console-v2/register";

export function isAuthConfigured(): boolean {
  return !!(KEYCLOAK_URL && KEYCLOAK_REALM && KEYCLOAK_CLIENT_ID);
}

// ─── Keycloak singleton ───────────────────────────────────────────────────────
let kc: Keycloak | null = null;

function getInstance(): Keycloak {
  if (kc) return kc;
  if (!isAuthConfigured()) {
    throw new Error(
      "Keycloak is not configured. Set VITE_KEYCLOAK_URL, VITE_KEYCLOAK_REALM, VITE_KEYCLOAK_CLIENT_ID in .env"
    );
  }
  kc = new Keycloak({
    url: KEYCLOAK_URL!,
    realm: KEYCLOAK_REALM!,
    clientId: KEYCLOAK_CLIENT_ID!,
  });
  // Auto-refresh token 30s before expiry so callers always get a fresh one.
  kc.onTokenExpired = () => {
    kc?.updateToken(60).catch((e) => {
      // eslint-disable-next-line no-console
      console.warn("Keycloak token refresh failed", e);
    });
  };
  return kc;
}

export type AuthUser = {
  id: string;
  username?: string;
  email?: string;
  fullName?: string;
};

type AuthListener = (u: AuthUser | null) => void;
const authListeners: Set<AuthListener> = new Set();

/**
 * Subscribe to auth/consumer state changes. Returns an unsubscribe function.
 * Multiple components can subscribe independently — use this instead of the
 * legacy single-listener API.
 */
export function addAuthListener(fn: AuthListener): () => void {
  authListeners.add(fn);
  return () => { authListeners.delete(fn); };
}

/**
 * @deprecated Use addAuthListener() — single-listener API leaks when more
 * than one component subscribes. Kept for backward compat: setting null
 * removes ALL listeners (legacy clear behaviour); setting a fn ADDS it.
 */
export function setAuthChangeListener(fn: AuthListener | null) {
  if (fn === null) {
    authListeners.clear();
    return;
  }
  authListeners.add(fn);
}

function emitChange() {
  const u = getUser();
  for (const fn of authListeners) {
    try { fn(u); } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("auth listener threw", e);
    }
  }
}

/**
 * Initialise Keycloak. Call once at app boot.
 * - On reload, attempts SSO via silent-check iframe so already-logged-in users
 *   don't get bounced to the login page.
 * - Resolves with `true` if authenticated, `false` otherwise.
 */
export async function initAuth(): Promise<boolean> {
  if (!isAuthConfigured()) return false;
  const k = getInstance();
  try {
    const authenticated = await k.init({
      onLoad: "check-sso",
      silentCheckSsoRedirectUri: window.location.origin + import.meta.env.BASE_URL + "silent-check-sso.html",
      pkceMethod: "S256",
      checkLoginIframe: false,
    });
    emitChange();
    return authenticated;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("Keycloak init failed", e);
    return false;
  }
}

export function isAuthenticated(): boolean {
  return !!kc?.authenticated;
}

export function getUser(): AuthUser | null {
  if (!kc?.authenticated) return null;
  const tok = kc.tokenParsed as any | undefined;
  if (!tok) return null;
  return {
    id: tok.sub,
    username: tok.preferred_username,
    email: tok.email,
    fullName: tok.name || tok.preferred_username,
  };
}

// ─── Console roles ────────────────────────────────────────────────────────────
// Same claim the Vietmap Console reads: client roles of the `console` client in
// the access token (`resource_access.console.roles`).
//   SystemAdmins    — every account
//   AccountManagers — accounts they manage
//   AccountUsers    — regular customer
// UI gating only; the API checks the verified token itself.
const ROLE_CLIENT = (import.meta.env.VITE_KEYCLOAK_ROLE_CLIENT as string | undefined) || "console";
export const ROLE_SYSTEM_ADMIN = "SystemAdmins";
export const ROLE_ACCOUNT_MANAGER = "AccountManagers";

export function getConsoleRoles(): string[] {
  const roles = (kc?.authenticated && (kc.tokenParsed as any)?.resource_access?.[ROLE_CLIENT]?.roles) || [];
  return Array.isArray(roles) ? roles : [];
}

/** Can open the admin site (SystemAdmins or AccountManagers). */
export function isAdminUser(): boolean {
  const roles = getConsoleRoles();
  return roles.includes(ROLE_SYSTEM_ADMIN) || roles.includes(ROLE_ACCOUNT_MANAGER);
}

// ─── Consumer (tenant) resolution ─────────────────────────────────────────────
// The Map.Editor.Api needs an `X-Consumer-ID` header for every call. We get it
// from Vietmap's console API:
//   GET https://maps.vietmap.vn/console/api/accounts/{userId}/consumers
// The user may have multiple consumers — for now we auto-pick the first one
// and expose `setActiveConsumerId` so a future UI picker can change it.

/** Rate-limit metadata row from `/consumers/{id}/limits`. */
export type ConsumerLimit = {
  apiId: number;
  enable: boolean;
  perSecond?: number | null;
  perMinute?: number | null;
  perHour?: number | null;
  perDay?: number | null;
  perMonth?: number | null;
};

/** An actual usable API key token from `/consumers/{id}/apikeys`. */
export type ApiKeyEntry = {
  /** Identifier (id from the apikeys endpoint, may differ from apiId in limits). */
  id?: string | number;
  /** The actual token string used in tile URLs. */
  key: string;
  name?: string;
  /** Optional rate-limit hint carried for display. */
  perMinute?: number | null;
  /** Disabled in Console — tiles refuse it. */
  inactive?: boolean;
  /** Console "private" key (meant for servers); browsers should use a public one. */
  private?: boolean;
};

export type ConsumerRef = {
  id: string;
  name?: string;
  /** True if `/limits` shows `apiId=7 && enable=true`. */
  hasMapAccess: boolean;
  /** The apiId=7 limit row, for displaying rate caps. */
  mapLimit?: ConsumerLimit;
  /** Apikeys for this consumer. `null` = not yet fetched (lazy on selection). */
  apiKeys: ApiKeyEntry[] | null;
  apiKeysLoadState: ConsumersLoadState;
  apiKeysLoadError: string | null;
  /**
   * Load state for `/limits`. `idle` until the user selects this consumer,
   * then `loading` → `loaded`. We only fetch limits for the active consumer
   * to avoid spamming the backend with N requests on boot.
   */
  limitsLoadState: ConsumersLoadState;
};

/** apiId of the tile service when Console's API catalog can't be read. */
const FALLBACK_TILE_API_ID = 7;

// Under the app's own base (`/map-editor/console/api`) so a gateway only has to
// forward `/map-editor/` — `/console/` is usually taken by Vietmap Console
// itself. The container's nginx (and the Vite dev proxy) forward it to
// https://maps.vietmap.vn/console/api. Override with VITE_CONSUMERS_API_URL.
const CONSUMERS_API_BASE = (import.meta.env.VITE_CONSUMERS_API_URL as string | undefined)
  || `${import.meta.env.BASE_URL}console/api`;

/**
 * Parse a Console API response. A reverse proxy that doesn't forward
 * the Console API path usually answers with some SPA's index.html — say so instead
 * of surfacing "Unexpected token '<'".
 */
async function readConsoleJson(res: Response): Promise<any> {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(
      `${CONSUMERS_API_BASE} trả về ${/^\s*</.test(text) ? "HTML" : "dữ liệu không phải JSON"} thay vì JSON — ` +
        `reverse proxy chưa chuyển ${CONSUMERS_API_BASE}/ tới https://maps.vietmap.vn/console/api/`
    );
  }
}

let consumers: ConsumerRef[] = [];
let activeConsumerId: string | null = null;

export type ConsumersLoadState = "idle" | "loading" | "loaded" | "error";
let consumersLoadState: ConsumersLoadState = "idle";
let consumersLoadError: string | null = null;
let consumersInflight: Promise<ConsumerRef[]> | null = null;

export function getConsumersLoadState(): ConsumersLoadState {
  return consumersLoadState;
}

export function getConsumersLoadError(): string | null {
  return consumersLoadError;
}

// ─── Accounts ─────────────────────────────────────────────────────────────────
// A Keycloak user can own multiple Vietmap accounts; each account owns
// multiple consumers; each consumer has apikeys. The hierarchy is:
//   account → consumer → apikey

export type AccountRef = {
  id: string;
  name?: string;
  email?: string;
};

let accounts: AccountRef[] = [];
let activeAccountId: string | null = null;
let accountsLoadState: ConsumersLoadState = "idle";
let accountsLoadError: string | null = null;
let accountsInflight: Promise<AccountRef[]> | null = null;

export function getAccounts(): AccountRef[] {
  return accounts;
}

export function getActiveAccountId(): string | null {
  return activeAccountId;
}

export function getActiveAccount(): AccountRef | null {
  return accounts.find((a) => a.id === activeAccountId) || null;
}

export function getAccountsLoadState(): ConsumersLoadState {
  return accountsLoadState;
}

export function getAccountsLoadError(): string | null {
  return accountsLoadError;
}

/**
 * Switch active account. Clears the consumer cache and triggers a re-fetch
 * for the new account. Returns the inflight Promise so callers can await
 * the new consumer list if needed.
 */
export function setActiveAccountId(id: string): Promise<ConsumerRef[]> {
  if (!accounts.some((a) => a.id === id)) return Promise.resolve(consumers);
  if (activeAccountId === id) return Promise.resolve(consumers);
  activeAccountId = id;
  try { localStorage.setItem("vmp:active_account_id", id); } catch { /* ignore */ }
  // The old account's key must not stay in use — and pages should wait for
  // the new one rather than reload with the old.
  forceKeySync = true;
  keyResolving = true;
  // Reset consumers; they're scoped to the account.
  consumers = [];
  activeConsumerId = null;
  consumersLoadState = "idle";
  consumersLoadError = null;
  emitChange();
  return loadConsumers({ force: true });
}

/**
 * Fetch the list of Vietmap accounts the user belongs to.
 * - Re-entrancy safe.
 * - Pass `{force: true}` to retry after a failure.
 */
export async function loadAccounts(opts: { force?: boolean } = {}): Promise<AccountRef[]> {
  if (!kc?.authenticated) return [];
  if (accountsInflight) return accountsInflight;
  if (!opts.force && accountsLoadState === "loaded") return accounts;

  accountsLoadState = "loading";
  accountsLoadError = null;
  emitChange();

  accountsInflight = (async () => {
    try {
      await kc!.updateToken(30);
      const res = await fetch(`${CONSUMERS_API_BASE}/accounts`, {
        headers: { Authorization: `Bearer ${kc!.token}` },
      });
      if (!res.ok) {
        accountsLoadState = "error";
        accountsLoadError = `HTTP ${res.status}: ${res.statusText}`;
        emitChange();
        return [];
      }
      const raw = await readConsoleJson(res);
      const list: any[] = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.data) ? raw.data
        : Array.isArray(raw?.items) ? raw.items
        : [];
      accounts = list
        .map((a) => ({
          id: String(a.id || a.accountId || a.account_id || ""),
          name: a.name || a.fullName || a.displayName || a.username,
          email: a.email,
        }))
        .filter((a) => a.id)
        .sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id));

      // Restore saved active account if still valid, else first.
      let saved: string | null = null;
      try { saved = localStorage.getItem("vmp:active_account_id"); } catch { /* private mode */ }
      const hit = saved ? accounts.find((a) => a.id === saved) : null;
      activeAccountId = hit?.id || accounts[0]?.id || null;

      accountsLoadState = "loaded";
      accountsLoadError = null;
      emitChange();
      return accounts;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("loadAccounts failed", e);
      accountsLoadState = "error";
      accountsLoadError = e instanceof Error ? e.message : String(e);
      emitChange();
      return [];
    }
  })().finally(() => {
    accountsInflight = null;
  });

  return accountsInflight;
}

/**
 * Fetch the list of consumers the current user belongs to, then fetch each
 * consumer's apikey list and determine which ones have `apiId=7` enabled.
 * Consumers without map access are marked `hasMapAccess=false` and sorted to
 * the bottom of the list.
 *
 * - Re-entrancy safe: parallel calls share the same in-flight Promise.
 * - Pass `{force: true}` to retry after a failure.
 * - Called automatically after init when authenticated, AND lazily by the
 *   toolbar when the user opens the consumer picker.
 */
export async function loadConsumers(opts: { force?: boolean } = {}): Promise<ConsumerRef[]> {
  if (!kc?.authenticated) return [];

  // De-dupe concurrent calls.
  if (consumersInflight) return consumersInflight;
  // Cached: don't re-fetch unless forced.
  if (!opts.force && consumersLoadState === "loaded") return consumers;

  consumersLoadState = "loading";
  consumersLoadError = null;
  keyResolving = true;
  emitChange();

  consumersInflight = (async () => {
  try {
    await kc!.updateToken(30);
    // Step 1: ensure accounts are loaded, then use the active account id.
    if (accountsLoadState !== "loaded") {
      await loadAccounts({ force: opts.force });
    }
    if (!activeAccountId) {
      consumersLoadState = "error";
      keyResolving = false;
      consumersLoadError = "No active account selected";
      emitChange();
      return [];
    }
    // Step 2: list consumers under that account.
    const res = await fetch(`${CONSUMERS_API_BASE}/accounts/${activeAccountId}/consumers`, {
      headers: { Authorization: `Bearer ${kc!.token}` },
    });
    if (!res.ok) {
      // eslint-disable-next-line no-console
      console.warn("loadConsumers HTTP error", res.status);
      consumersLoadState = "error";
      keyResolving = false;
      consumersLoadError = `HTTP ${res.status}: ${res.statusText}`;
      emitChange();
      return [];
    }
    const raw = await readConsoleJson(res);
    // Accept multiple response shapes: array, { data: [...] }, { items: [...] }
    const list: any[] = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.data) ? raw.data
      : Array.isArray(raw?.items) ? raw.items
      : [];

    // First pass: shallow consumer records.
    const partials = list
      .map((c) => ({
        id: String(c.id || c.consumerId || c.consumer_id || ""),
        name: c.name || c.consumerName || c.displayName,
      }))
      .filter((c) => c.id);

    // Build the consumer list without calling /limits — we only fetch /limits
    // for the consumer that gets activated (saved or first), to avoid sending
    // N requests on boot. Limits for other consumers load lazily when the
    // user selects them.
    const enriched: ConsumerRef[] = partials.map((p) => {
      const cached = getCachedApiKeys(p.id);
      return {
        id: p.id,
        name: p.name,
        hasMapAccess: true, // optimistic — corrected once /limits resolves
        mapLimit: undefined,
        apiKeys: cached,
        apiKeysLoadState: cached ? ("loaded" as ConsumersLoadState) : ("idle" as ConsumersLoadState),
        apiKeysLoadError: null,
        limitsLoadState: "idle" as ConsumersLoadState,
      };
    });

    // Stable sort by name (no map-access info yet).
    enriched.sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id));
    consumers = enriched;

    // Pick the active consumer:
    //   1. Previously-saved selection
    //   2. First consumer in the list (fallback so UI isn't stuck)
    let saved: string | null = null;
    try { saved = localStorage.getItem("vmp:active_consumer_id"); } catch { /* private mode */ }
    const savedHit = saved ? enriched.find((c) => c.id === saved) : null;
    activeConsumerId = savedHit?.id || bestTileConsumer(enriched)?.id || enriched[0]?.id || null;

    consumersLoadState = "loaded";
    consumersLoadError = null;
    emitChange();

    // Fire-and-forget: fetch /limits only for the active consumer.
    if (activeConsumerId) {
      void loadConsumerLimits(activeConsumerId);
    }
    void syncDefaultApiKey();
    return consumers;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("loadConsumers failed", e);
    consumersLoadState = "error";
    keyResolving = false;
    consumersLoadError = e instanceof Error ? e.message : String(e);
    emitChange();
    return [];
  }
  })().finally(() => {
    consumersInflight = null;
  });

  return consumersInflight;
}

/**
 * Fetch the rate-limit table for one consumer. Each row carries `apiId`,
 * `enable`, and per-second/minute/hour/day/month limits. Used to determine
 * whether a consumer has the required service (apiId=7) enabled.
 */
async function fetchConsumerLimits(consumerId: string): Promise<ConsumerLimit[]> {
  if (!kc?.token) return [];
  try {
    const res = await fetch(
      `${CONSUMERS_API_BASE}/consumers/${consumerId}/limits`,
      { headers: { Authorization: `Bearer ${kc.token}` } }
    );
    if (!res.ok) return [];
    const raw = await readConsoleJson(res);
    const list: any[] = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.data) ? raw.data
      : Array.isArray(raw?.items) ? raw.items
      : [];
    return list.map((row: any): ConsumerLimit => ({
      apiId: Number(row.apiId ?? row.api_id ?? -1),
      enable: Boolean(row.enable ?? row.enabled),
      perSecond: row.perSecond ?? null,
      perMinute: row.perMinute ?? null,
      perHour: row.perHour ?? null,
      perDay: row.perDay ?? null,
      perMonth: row.perMonth ?? null,
    }));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("fetchConsumerLimits failed", consumerId, e);
    return [];
  }
}

const limitsInflight: Map<string, Promise<void>> = new Map();

let tileApiIds: Promise<Set<number>> | null = null;

/**
 * Ids of Console's tile APIs: `GET /apis` lists every API ({id, name, path});
 * Console itself treats the one whose name contains "tile" as Tilemap.
 * Falls back to apiId 7 when the catalog can't be read.
 */
function loadTileApiIds(): Promise<Set<number>> {
  tileApiIds ??= (async () => {
    if (!kc?.token) throw new Error("Not authenticated");
    const res = await fetch(`${CONSUMERS_API_BASE}/apis`, { headers: { Authorization: `Bearer ${kc.token}` } });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const raw = await readConsoleJson(res);
    const list: any[] = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
    const ids = new Set(list.filter((a) => /tile/i.test(String(a?.name ?? ""))).map((a) => Number(a.id)));
    if (!ids.size) throw new Error("no tile API in the catalog");
    return ids;
  })().catch((e) => {
    // eslint-disable-next-line no-console
    console.warn("Console /apis unavailable, assuming tile apiId", FALLBACK_TILE_API_ID, e);
    tileApiIds = null; // retry next time
    return new Set([FALLBACK_TILE_API_ID]);
  });
  return tileApiIds;
}

/**
 * Lazily fetch /limits for one consumer to determine whether its key can load
 * map tiles (the Tilemap API, see loadTileApiIds), and update the consumer record's `hasMapAccess` + `mapLimit`.
 * Called for the active consumer at boot and again whenever the user picks
 * a different consumer in the toolbar. De-duped via {@link limitsInflight}.
 */
export async function loadConsumerLimits(consumerId: string): Promise<void> {
  const consumer = consumers.find((c) => c.id === consumerId);
  if (!consumer) return;
  if (consumer.limitsLoadState === "loaded") return;
  const existing = limitsInflight.get(consumerId);
  if (existing) return existing;

  consumer.limitsLoadState = "loading";
  emitChange();

  const promise = (async () => {
    try {
      const [limits, tileIds] = await Promise.all([fetchConsumerLimits(consumerId), loadTileApiIds()]);
      // Same rule as Console (api-access-warning): an API is open when its
      // limit has `enable`; the tile API is the one whose name has "tile".
      const mapLimit = limits.find((l) => l.enable && tileIds.has(l.apiId));
      consumer.hasMapAccess = !!mapLimit;
      consumer.mapLimit = mapLimit;
      consumer.limitsLoadState = "loaded";
      emitChange();
    } finally {
      limitsInflight.delete(consumerId);
    }
  })();
  limitsInflight.set(consumerId, promise);
  return promise;
}

const apiKeysInflight: Map<string, Promise<ApiKeyEntry[]>> = new Map();

/**
 * Lazily fetch the actual API key tokens for one consumer. Called only when
 * the user selects a consumer in the picker (or for auto-apply when there's
 * exactly one active consumer at boot).
 *
 * Result is cached on the consumer record (`consumer.apiKeys`). Re-entrant
 * safe via inflight Map.
 */
export async function loadConsumerApiKeys(
  consumerId: string,
  opts: { force?: boolean } = {}
): Promise<ApiKeyEntry[]> {
  const consumer = consumers.find((c) => c.id === consumerId);
  if (!consumer) return [];
  if (!opts.force && consumer.apiKeysLoadState === "loaded" && consumer.apiKeys) {
    return consumer.apiKeys;
  }
  // De-dupe concurrent calls for the same consumer.
  const existing = apiKeysInflight.get(consumerId);
  if (existing) return existing;
  // Belt-and-suspenders: if state is already "loading" but Map miss (should
  // not happen, but cheap to guard), bail.
  if (consumer.apiKeysLoadState === "loading") return Promise.resolve([]);

  // IMPORTANT: install the inflight Promise into the Map BEFORE emitting any
  // state change. Otherwise a listener that calls back into this function
  // would bypass the dedupe check and fire another HTTP request.
  const promise = (async () => {
    try {
      if (!kc?.token) throw new Error("Not authenticated");
      await kc.updateToken(30);
      const res = await fetch(
        `${CONSUMERS_API_BASE}/consumers/${consumerId}/apikeys`,
        { headers: { Authorization: `Bearer ${kc.token}` } }
      );
      if (!res.ok) {
        consumer.apiKeysLoadState = "error";
        consumer.apiKeysLoadError = `HTTP ${res.status}: ${res.statusText}`;
        consumer.apiKeys = [];
        emitChange();
        return [];
      }
      const raw = await readConsoleJson(res);
      const list: any[] = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.data) ? raw.data
        : Array.isArray(raw?.items) ? raw.items
        : [];
      const parsed = parseApiKeys(list, consumerId);
      consumer.apiKeys = parsed;
      consumer.apiKeysLoadState = "loaded";
      consumer.apiKeysLoadError = null;
      // Cache for next session — avoids repeat /apikeys calls.
      setCachedApiKeys(consumerId, parsed);
      emitChange();
      return parsed;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("loadConsumerApiKeys failed", consumerId, e);
      consumer.apiKeysLoadState = "error";
      consumer.apiKeysLoadError = e instanceof Error ? e.message : String(e);
      consumer.apiKeys = [];
      emitChange();
      return [];
    }
  })().finally(() => {
    apiKeysInflight.delete(consumerId);
  });

  apiKeysInflight.set(consumerId, promise);

  // Now safe to flip state + emit — recursive listeners will hit the inflight
  // Map and reuse this Promise instead of firing another fetch.
  consumer.apiKeysLoadState = "loading";
  consumer.apiKeysLoadError = null;
  emitChange();

  return promise;
}

function parseApiKeys(list: any[], consumerId: string): ApiKeyEntry[] {
  return list
    .map((row: any): ApiKeyEntry => ({
      id: row.id ?? row.apiKeyId ?? row.apikey_id,
      key: String(
        row.apikey || row.apiKey || row.key || row.token || row.value || consumerId
      ),
      name: row.name || row.label,
      perMinute: row.perMinute ?? null,
      inactive: row.inactive === true,
      private: row.private === true,
    }))
    .filter((k) => k.key);
}


// ─── Default API key ─────────────────────────────────────────────────────────
// The key only loads tiles, so it comes from a consumer of the active account
// whose Tilemap API is enabled (Console limits) — "public tile" first when
// several are. Re-picked when there is no key, after an account switch, and
// when the stored key belongs to another account, to a consumer without tile
// access, or was disabled. A key typed by hand (no consumer of the account
// owns it) is kept until the account changes. Every change emits, so pages
// re-render / re-apply it (useApiKey, App).

const TILE_CONSUMER_PATTERNS = [/public[\s_-]*tiles?/i, /tiles?/i];

/** Next sync replaces the stored key whatever its owner (account switch). */
let forceKeySync = false;
let keySyncSeq = 0;
/** A default key is being looked up — UI shows "loading" instead of asking. */
let keyResolving = false;
/** The sync in flight, so a recovery can wait for it instead of racing it. */
let syncInflight: Promise<void> | null = null;
/** Keys the tile server refused (401/403) this session — never picked again. */
const rejectedKeys = new Set<string>();
/** Next sync re-reads limits and key lists from Console (not the cache). */
let refreshKeySync = false;

export function isApiKeyResolving(): boolean {
  return keyResolving;
}

function tileRank(name: string | undefined): number {
  const i = TILE_CONSUMER_PATTERNS.findIndex((re) => re.test(name || ""));
  return i === -1 ? Infinity : i;
}

function bestTileConsumer<T extends { name?: string }>(list: T[]): T | null {
  let best: T | null = null;
  for (const c of list) if (tileRank(c.name) < tileRank(best?.name)) best = c;
  return best && tileRank(best.name) < Infinity ? best : null;
}

/** Best usable key: active only; public before private; "tile"-named first. */
function pickKey(keys: ApiKeyEntry[]): ApiKeyEntry | null {
  const usable = keys.filter((k) => !k.inactive && !rejectedKeys.has(k.key));
  const rank = (k: ApiKeyEntry) => (k.private ? 2 : 0) + (/tile/i.test(k.name || "") ? 0 : 1);
  return usable.sort((a, b) => rank(a) - rank(b))[0] ?? null;
}

function syncDefaultApiKey(): Promise<void> {
  const run = runKeySync();
  syncInflight = run;
  void run.finally(() => {
    if (syncInflight === run) syncInflight = null;
  });
  return run;
}

async function runKeySync(): Promise<void> {
  if (!kc?.authenticated || !consumers.length) {
    keyResolving = false;
    return;
  }
  const force = forceKeySync;
  forceKeySync = false;
  const refresh = refreshKeySync;
  refreshKeySync = false;
  const seq = ++keySyncSeq;
  const stale = () => seq !== keySyncSeq;
  keyResolving = true;
  emitChange();
  try {
    // Which consumers can load tiles (one /limits call each, cached).
    if (refresh) for (const c of consumers) c.limitsLoadState = "idle";
    await Promise.all(consumers.map((c) => loadConsumerLimits(c.id)));
    if (stale()) return;

    const stored = getStoredApiKey();
    let owner = getStoredApiKeyOwner();
    if (stored && !force) {
      // Keys saved by older versions carry no owner — find it in this account.
      if (!owner) {
        for (const c of consumers) {
          if ((await loadConsumerApiKeys(c.id)).some((k) => k.key === stored)) {
            owner = c.id;
            writeKey(stored, owner);
            break;
          }
        }
        if (stale()) return;
      }
      const ownerConsumer = owner ? consumers.find((c) => c.id === owner) : null;
      const ownerKey = ownerConsumer?.apiKeys?.find((k) => k.key === stored);
      // Kept: a key of this account's tile consumer, or one typed by hand
      // while signed in to this account. Anything else — another account's,
      // saved by an older version, typed while signed out — makes way for
      // the account's Tilemap key.
      const kept = ownerConsumer ? ownerConsumer.hasMapAccess && !ownerKey?.inactive : owner === manualOwner();
      const usable = kept && !rejectedKeys.has(stored);
      if (usable) return;
    }

    const rank = (c: ConsumerRef) => (c.hasMapAccess ? 0 : 10) + Math.min(tileRank(c.name), 5) + (c.id === activeConsumerId ? 0 : 0.5);
    const candidates = [...consumers].sort((a, b) => rank(a) - rank(b));
    for (const c of candidates) {
      const key = pickKey(await loadConsumerApiKeys(c.id, { force: refresh }));
      // A newer sync (another account switch) or a pick by the user wins.
      if (stale()) return;
      if (!key) continue;
      writeKey(key.key, c.id);
      return;
    }
  } finally {
    if (!stale()) {
      keyResolving = false;
      emitChange();
    }
  }
}

/** Owner tag of a key typed by hand while signed in to the active account. */
function manualOwner(): string | null {
  return activeAccountId ? `manual:${activeAccountId}` : null;
}

/**
 * The tile server refused `rejected` (401/403), or the map has no key at all:
 * reload what failed to load and pick the account's Tilemap key again, fresh
 * from Console and skipping refused keys. Resolves true when a different key
 * is now stored. Each key is refused at most once, so this can't loop.
 */
export async function recoverApiKey(rejected: string | null): Promise<boolean> {
  if (!kc?.authenticated) return false;
  if (rejected) rejectedKeys.add(rejected);
  // A lookup already running (first sign-in, slow Console) may still bring a
  // good key — wait for it before starting another.
  if (consumersInflight) await consumersInflight;
  if (syncInflight) await syncInflight;
  const good = () => {
    const k = getStoredApiKey();
    return !!k && k !== rejected && !rejectedKeys.has(k);
  };
  if (good()) return true;
  if (consumersLoadState !== "loaded" || !consumers.length) {
    await loadConsumers({ force: true }); // starts its own sync
    if (syncInflight) await syncInflight;
    if (good()) return true;
  }
  refreshKeySync = true;
  await syncDefaultApiKey();
  return good();
}

/** Drop a hand-typed key and pick the account's Tilemap key again. */
export function resyncDefaultApiKey(): Promise<void> {
  forceKeySync = true;
  return syncDefaultApiKey();
}

/** Where the key in use comes from — shown in the API-key picker. */
export function getApiKeySource(): { kind: "consumer"; consumer: ConsumerRef } | { kind: "manual" } | { kind: "none" } {
  if (!getStoredApiKey()) return { kind: "none" };
  const consumer = getApiKeyConsumer();
  return consumer ? { kind: "consumer", consumer } : { kind: "manual" };
}

/** Consumer whose key is in use (null: typed by hand / unknown). */
export function getApiKeyConsumer(): ConsumerRef | null {
  const owner = getStoredApiKeyOwner();
  return owner ? consumers.find((c) => c.id === owner) ?? null : null;
}

export function getConsumers(): ConsumerRef[] {
  return consumers;
}

export function setActiveConsumerId(id: string): void {
  if (!consumers.some((c) => c.id === id)) return;
  activeConsumerId = id;
  try { localStorage.setItem("vmp:active_consumer_id", id); } catch { /* ignore */ }
  emitChange();
  // Lazy: fetch /limits for the newly-selected consumer if we haven't yet.
  void loadConsumerLimits(id);
}

// ─── Picked API key (persisted across routes) ─────────────────────────────────
// The actual token string the user picked from the consumer/apikey popover.
// Used so the picker works on /manage too: the editor reads this on mount
// and injects into mapStyle metadata for tile loading.
const ACTIVE_KEY_STORAGE = "vmp:active_apikey";

export function getStoredApiKey(): string | null {
  try { return localStorage.getItem(ACTIVE_KEY_STORAGE); } catch { return null; }
}

const ACTIVE_KEY_OWNER_STORAGE = "vmp:active_apikey_consumer";

/**
 * `consumerId` = the consumer the key was taken from (picker / default);
 * omit it for a key typed by hand.
 */
export function setStoredApiKey(key: string, consumerId: string | null = null): void {
  // Any explicit choice supersedes a default-key lookup still in flight.
  keySyncSeq++;
  keyResolving = false;
  // Typed by hand: remember the account it was typed in (kept there only).
  writeKey(key, consumerId ?? (kc?.authenticated ? manualOwner() : null));
}

/** Store + notify (every page reading the key re-renders / re-applies it). */
function writeKey(key: string, consumerId: string | null) {
  const changed = getStoredApiKey() !== key || getStoredApiKeyOwner() !== consumerId;
  try {
    localStorage.setItem(ACTIVE_KEY_STORAGE, key);
    if (consumerId) localStorage.setItem(ACTIVE_KEY_OWNER_STORAGE, consumerId);
    else localStorage.removeItem(ACTIVE_KEY_OWNER_STORAGE);
  } catch { /* ignore */ }
  if (changed) emitChange();
}

/** Consumer the stored key belongs to; null when typed by hand or unknown. */
export function getStoredApiKeyOwner(): string | null {
  try { return localStorage.getItem(ACTIVE_KEY_OWNER_STORAGE); } catch { return null; }
}

// ─── Cached apikey list per consumer ─────────────────────────────────────────
// We cache the response from `/consumers/{id}/apikeys` so the picker doesn't
// have to re-fetch on every session. 24h TTL — long enough to avoid spam,
// short enough that newly-issued keys appear within a day. User can also
// force a re-fetch via the "↻ refresh" button on the picker.

// v2: entries carry `inactive` / `private` (older caches lack them).
const APIKEYS_CACHE_PREFIX = "vmp:apikeys2:";
const APIKEYS_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

function getCachedApiKeys(consumerId: string): ApiKeyEntry[] | null {
  try {
    const raw = localStorage.getItem(APIKEYS_CACHE_PREFIX + consumerId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { t: number; keys: ApiKeyEntry[] };
    if (Date.now() - parsed.t > APIKEYS_CACHE_TTL_MS) return null;
    return parsed.keys;
  } catch {
    return null;
  }
}

function setCachedApiKeys(consumerId: string, keys: ApiKeyEntry[]): void {
  try {
    localStorage.setItem(
      APIKEYS_CACHE_PREFIX + consumerId,
      JSON.stringify({ t: Date.now(), keys })
    );
  } catch { /* ignore quota errors */ }
}

export function getActiveConsumer(): ConsumerRef | null {
  return consumers.find((c) => c.id === activeConsumerId) || null;
}


/**
 * Returns true once `loadConsumers()` has resolved (regardless of result).
 * UI can wait on this to render the picker only when data is ready.
 */
export function consumersLoaded(): boolean {
  return consumers.length > 0;
}

/**
 * Returns the `X-Consumer-ID` header value.
 * Priority: explicit picker selection > first consumer from API >
 *           token claim > env var fallback.
 */
export function getConsumerId(): string | null {
  if (activeConsumerId) return activeConsumerId;
  const tok = (kc?.tokenParsed || {}) as any;
  const fromToken =
    tok.consumer_id ||
    tok.consumerId ||
    tok.tenant_id ||
    tok.tenantId ||
    tok.org_id ||
    tok.organization_id;
  if (fromToken) return String(fromToken);
  const fromEnv = import.meta.env.VITE_DEFAULT_CONSUMER_ID as string | undefined;
  return fromEnv || null;
}

/**
 * Get a valid access token, auto-refreshing if it expires within 30s.
 * Returns null if not authenticated.
 */
export async function getToken(): Promise<string | null> {
  if (!kc?.authenticated) return null;
  try {
    await kc.updateToken(30);
    return kc.token ?? null;
  } catch {
    return null;
  }
}

export function login(redirectUri?: string): void {
  // keycloak-js needs crypto.randomUUID / crypto.subtle (PKCE), which browsers
  // only expose on secure origins: https:// or localhost. Plain http on a LAN
  // IP would otherwise fail with an uncaught "Web Crypto API is not available".
  if (!window.isSecureContext) {
    toast.error(
      i18n.t("Không đăng nhập được qua http://{{host}}. Hãy mở editor bằng HTTPS (hoặc localhost).", {
        host: window.location.host,
      })
    );
    return;
  }
  const k = getInstance();
  k.login({
    redirectUri: redirectUri || window.location.href,
  });
}

export function logout(redirectUri?: string): void {
  if (!kc) return;
  kc.logout({
    redirectUri: redirectUri || window.location.origin + import.meta.env.BASE_URL,
  });
}
